import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import BrandLogo from '../components/BrandLogo';
import {
  secsData,
  getAllMessages,
  formatMessageId,
  getDirectionLabel,
  getMessageTypeLabel,
} from '../utils/secsDataLoader';
import { getAllDataItems } from '../utils/dataItems';
import type { SecsMessage } from '../types/secs';

function MessageFrame({ msg }: { msg: SecsMessage }) {
  const id = formatMessageId(msg.stream, msg.function);
  const type = getMessageTypeLabel(msg.messageType);
  const direction = getDirectionLabel(msg.direction);

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="font-mono font-bold text-primary-600 dark:text-primary-400">{id}</span>
        <span className={`badge ${type.color}`}>{type.text}</span>
      </div>
      <p className="text-sm font-medium mb-1">{msg.name}</p>
      <p className={`text-xs font-mono ${direction.color}`}>{direction.text}</p>
    </div>
  );
}

const ENTRY_ICONS = {
  streams: 'M12 2l9 5-9 5-9-5 9-5zM3 12l9 5 9-5M3 17l9 5 9-5',
  dataItems: 'M7 7h.01M7 3h5.586a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5.586 5.586a2 2 0 01-2.828 0l-7-7A2 2 0 013 12.586V7a4 4 0 014-4z',
  gem: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 7h10v10H7V7z',
};

export default function Landing() {
  const navigate = useNavigate();
  const { setSearchQuery } = useAppStore();
  const [query, setQuery] = useState('');

  const stats = useMemo(() => ({
    streams: Object.keys(secsData.streams).length,
    messages: getAllMessages().length,
    dataItems: getAllDataItems().length,
  }), []);

  const primaryMsg = secsData.streams['S1']?.functions['F1'];
  const replyMsg = secsData.streams['S1']?.functions['F2'];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchQuery(query);
      navigate('/search');
    }
  };

  const entries = [
    {
      key: 'streams',
      accent: 'primary' as const,
      title: 'Browse Streams & Functions',
      description: 'Explore the full Stream/Function hierarchy — from equipment status through to GEM events.',
      cta: 'Start with S1F1',
      to: '/message/S1F1',
    },
    {
      key: 'dataItems',
      accent: 'secondary' as const,
      title: 'Data Item Dictionary',
      description: "Look up every data item's type, length, and valid codes in one place.",
      cta: 'Browse Data Items',
      to: '/data-items',
    },
    {
      key: 'gem',
      accent: 'primary' as const,
      title: 'GEM Capabilities',
      description: 'State models and capabilities defined by the Generic Equipment Model.',
      cta: 'View GEM Model',
      to: '/gem',
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Hero */}
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center py-8 lg:py-12">
        <div className="animate-fade-up">
          <BrandLogo variant="full" className="h-16 md:h-20 w-auto rounded-xl shadow-sm ring-1 ring-black/5 mb-6" />

          <p className="font-mono text-xs uppercase tracking-wider text-[var(--color-text-tertiary)] mb-3">
            SEMI E5 · SECS-II / GEM
          </p>
          <h1 className="font-mono text-4xl md:text-5xl font-bold tracking-tight mb-4 text-balance">
            SECS-II / GEM Reference
          </h1>
          <p className="text-lg text-[var(--color-text-secondary)] mb-6">
            Look up SECS-II message formats and data item definitions without digging through the standard.
          </p>

          <form onSubmit={handleSearch} className="flex gap-2 mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search messages, functions, or data items..."
              className="input"
              aria-label="Quick search"
            />
            <button type="submit" className="btn btn-primary shrink-0">
              Search
            </button>
          </form>
          <button onClick={() => navigate('/message/S1F1')} className="link text-sm">
            Jump to S1F1 →
          </button>
        </div>

        <div className="card p-5 sm:p-6 animate-fade-up" style={{ animationDelay: '80ms' }}>
          <p className="font-mono text-xs uppercase tracking-wider text-[var(--color-text-tertiary)] mb-4">
            Primary / Reply Pair
          </p>
          {primaryMsg && replyMsg ? (
            <div className="space-y-3">
              <MessageFrame msg={primaryMsg} />
              <div className="flex items-center gap-3 pl-1">
                <span className="text-[var(--color-text-tertiary)] font-mono text-xs">⇅ reply</span>
                <span className="flex-1 border-t border-dashed border-[var(--color-border)]" />
              </div>
              <MessageFrame msg={replyMsg} />
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">S1F1 / S1F2 reference unavailable.</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-12">
        {[
          { label: 'Streams', value: stats.streams },
          { label: 'Messages', value: stats.messages },
          { label: 'Data Items', value: stats.dataItems },
        ].map((stat) => (
          <div key={stat.label} className="card p-4 sm:p-6 text-center">
            <p className="font-mono text-3xl md:text-4xl font-bold text-primary-600 dark:text-primary-400">
              {stat.value}
            </p>
            <p className="text-xs uppercase tracking-wider text-[var(--color-text-tertiary)] mt-1">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* Entry points */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {entries.map((entry) => {
          const isPrimary = entry.accent === 'primary';
          return (
            <button
              key={entry.key}
              onClick={() => navigate(entry.to)}
              className="card p-6 text-left flex flex-col gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isPrimary
                    ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                    : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-900 dark:text-secondary-300'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ENTRY_ICONS[entry.key as keyof typeof ENTRY_ICONS]} />
                </svg>
              </div>
              <h2 className="font-semibold text-lg">{entry.title}</h2>
              <p className="text-sm text-[var(--color-text-secondary)] flex-1">{entry.description}</p>
              <span className={`text-sm font-medium ${isPrimary ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-600 dark:text-secondary-400'}`}>
                {entry.cta} →
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
